import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  TextareaAutosize,
  CircularProgress,
} from '@mui/material';
import { API } from 'aws-amplify';
import * as mutations from '../../../../graphql/mutations';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import { GamifyToast } from '../../../common/CustomToasts';
import { getTZ } from '../../../common/Utilities';

interface DealNotesProps {
  dealId: string;
  comments: any[];
  categoryId?: string;
  currentOwnerID?: string;
  onRefetch: () => void;
  showEdit?: boolean;
}

const DealNotes: React.FC<DealNotesProps> = ({
  dealId,
  comments,
  categoryId,
  currentOwnerID,
  onRefetch,
  showEdit = true,
}) => {
  const { theme } = useTheme();
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      ' ' +
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  };

  const handleSubmitNote = async () => {
    if (!newNote.trim()) return;

    setIsSubmitting(true);
    try {
      const now = new Date();
      const completeMissionInput = {
        currentOwnerID: currentOwnerID || '',
        userMissionID: '',
        completedFlag: false,
        myDoneActionCount: 1,
        doneImage: '',
        doneImageType: '',
        status: 'completed',
        extID: '',
        comments: newNote.trim(),
        dealID: dealId,
        dispositionID: '',
        followupTZ: getTZ(),
        contactType: '',
        categoryID: categoryId || '',
        tmst: now.toISOString(),
      };

      const res = (await API.graphql({
        query: mutations.completeMission,
        variables: { missionInfo: JSON.stringify(completeMissionInput) },
      })) as { data: { completeMission: string } };

      const status = JSON.parse(res.data.completeMission);
      if (status.status === 'success') {
        setNewNote('');
        setIsFocused(false);
        GamifyToast.success('Note added successfully');
        onRefetch();
      } else {
        GamifyToast.error('Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      GamifyToast.error('Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAddNote = () => (
    <Box sx={{ mb: 2 }}>
      <TextareaAutosize
        minRows={isFocused ? 3 : 1}
        placeholder="Add a note..."
        value={newNote}
        onChange={(e) => setNewNote(e.target.value)}
        onFocus={() => setIsFocused(true)}
        style={{
          width: '100%',
          padding: '12px',
          borderRadius: '8px',
          border: `1px solid ${theme.stroke}`,
          backgroundColor: theme.background1,
          color: theme.text,
          fontFamily: 'inherit',
          fontSize: '14px',
          resize: 'none',
        }}
      />
      {isFocused && (
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}
        >
          <Button
            onClick={() => {
              setIsFocused(false);
              setNewNote('');
            }}
            sx={{ color: theme.textLight }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitNote}
            disabled={!newNote.trim() || isSubmitting}
            variant="contained"
            sx={{
              backgroundColor: theme.primary,
              '&:hover': { backgroundColor: theme.primaryDark },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              'Save'
            )}
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderNoteItem = (comment: any, index: number) => (
    <Box
      key={comment.id || index}
      sx={{
        backgroundColor: theme.background2,
        borderRadius: 2,
        p: 2,
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={comment.user?.profilePicture}
            sx={{ width: 32, height: 32, fontSize: 12 }}
          >
            {comment.user?.initials || '?'}
          </Avatar>
          <Typography sx={{ fontWeight: 600, color: theme.text, fontSize: 14 }}>
            {comment.user?.name || 'Unknown'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: theme.textLight }}>
          {formatTime(comment.updatedAt || comment.createdAt)}
        </Typography>
      </Box>
      <Typography
        sx={{ color: theme.text, fontSize: 14, whiteSpace: 'pre-wrap' }}
      >
        {comment.comment}
      </Typography>
      {comment.disposition?.title && (
        <Box
          sx={{
            mt: 1,
            display: 'inline-block',
            backgroundColor: `${theme.primary}20`,
            borderRadius: 1,
            px: 1,
            py: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 11, color: theme.primary }}>
            {comment.disposition.title}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.text }}>
          Notes ({comments.length})
        </Typography>
      </Box>

      {showEdit && renderAddNote()}

      {comments.length > 0 ? (
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {comments.map((comment, index) => renderNoteItem(comment, index))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
          }}
        >
          <Typography sx={{ color: theme.textLight, textAlign: 'center' }}>
            No notes yet
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DealNotes;
